import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../config/axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import ReviewModeBanner from '../components/ReviewModeBanner';
import useProfileGate from '../hooks/useProfileGate';
import { AddressPicker } from '../components/profile/Addresses';
import { getMembership, redeemCredits, isMembershipActive, canPurchaseMembership } from '../utils/membership';
import {
  loadRazorpayScript,
  createRazorpayOrder,
  verifyRazorpayPayment,
  buildRazorpayPrefill,
  withRazorpayCheckoutConfig,
  pollRazorpayOrderStatus,
  describePaymentError,
} from '../utils/razorpay';

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Prevents both the Razorpay iframe handler AND our backend poller from
  // completing the order twice when a UPI Collect success arrives via both
  // channels simultaneously.
  const paymentCompletedRef = useRef(false);
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [membership, setMembership] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('gateway');
  const [paymentError, setPaymentError] = useState('');
  const [paymentErrorDetail, setPaymentErrorDetail] = useState('');
  const [fulfilmentMethod, setFulfilmentMethod] = useState('delivery');   // 'delivery' | 'pickup'
  const [addressId, setAddressId] = useState(null);
  const [pickupNote, setPickupNote] = useState('');
  const gate = useProfileGate();

  const fulfilment = () => (fulfilmentMethod === 'delivery'
    ? { method: 'delivery', address_id: addressId }
    : { method: 'pickup', note: pickupNote.trim() || null });

  // Before any money (or credits) moves: profile has a mobile number and the
  // delivery / pickup choice is valid. Returns false if checkout must stop.
  const preCheck = async () => {
    if (!(await gate.ensure('shop_checkout'))) return false;
    if (fulfilmentMethod === 'delivery' && !addressId) {
      toast.error('Choose a delivery address');
      return false;
    }
    try {
      await axiosInstance.post('/orders/check', { fulfilment: fulfilment() });
      return true;
    } catch (e) {
      if (await gate.handleError(e)) return preCheck();
      toast.error(e?.response?.data?.detail || 'Please check your delivery details');
      return false;
    }
  };

  const fetchCart = async () => {
    try {
      const response = await axiosInstance.get('/cart');
      if (response.data.items.length === 0) {
        toast.error('Your cart is empty');
        navigate('/shop');
        return;
      }
      setCart(response.data);
    } catch (error) {
      toast.error('Failed to load cart');
      navigate('/cart');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCart();
  }, []);

  useEffect(() => {
    const loadMembership = async () => {
      const membershipData = await getMembership();
      setMembership(membershipData);
    };
    loadMembership();
  }, []);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);

  const membershipActive = isMembershipActive(membership);
  const availableCredits = membershipActive ? membership.balance_remaining ?? 0 : 0;
  const canUseCredits = membershipActive && cart && availableCredits >= cart.total;
  const creditShortfall = cart ? Math.max(0, cart.total - availableCredits) : 0;

  useEffect(() => {
    if (!canUseCredits && paymentMethod === 'credits') {
      setPaymentMethod('gateway');
    }
  }, [canUseCredits, paymentMethod]);

  const completeOrder = async (method, razorpayIds = {}) => {
    await axiosInstance.post('/orders', {
      items: cart?.items || [],
      total: cart?.total || 0,
      payment_method: method,
      fulfilment: fulfilment(),
      razorpay_order_id:   razorpayIds.orderId   || null,
      razorpay_payment_id: razorpayIds.paymentId || null,
    });

    toast.success('Order placed successfully!');
    navigate('/profile');
  };

  const handleGatewayPayment = async () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    setPaymentError('');
    setPaymentErrorDetail('');
    paymentCompletedRef.current = false;
    let stopPolling = null;

    try {
      setProcessing(true);
      await loadRazorpayScript();

      const order = await createRazorpayOrder({
        backendUrl,
        amount: cart?.total || 0,
        receipt: `shop-${Date.now()}`,
      });

      const options = {
        key: order.key_id || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: 'Backyard',
        description: 'Shop order payment',
        order_id: order.order_id,
        prefill: buildRazorpayPrefill(),
        handler: async (response) => {
          if (paymentCompletedRef.current) return;
          paymentCompletedRef.current = true;
          stopPolling?.();
          try {
            await verifyRazorpayPayment({
              backendUrl,
              payload: {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
            });

            await completeOrder('gateway', {
              orderId:   response.razorpay_order_id,
              paymentId: response.razorpay_payment_id,
            });
          } catch (error) {
            console.error('Payment verification failed:', error);
            setPaymentError('Payment verification failed. Please try again.');
          } finally {
            setProcessing(false);
          }
        },
        modal: {
          ondismiss: () => {
            stopPolling?.();
            setProcessing(false);
          },
        },
        theme: {
          color: '#E64B65',
        },
      };

      const razorpayInstance = new window.Razorpay(withRazorpayCheckoutConfig(options));
      razorpayInstance.on('payment.failed', (response) => {
        stopPolling?.();
        setPaymentError(response?.error?.description || 'Payment failed. Please try again.');
        setProcessing(false);
      });

      // UPI Collect rescue: poll our backend (which polls Razorpay's server API)
      // in case the iframe's own success push never arrives. See utils/razorpay.js
      stopPolling = pollRazorpayOrderStatus({
        backendUrl,
        orderId: order.order_id,
        onPaid: async (info) => {
          if (paymentCompletedRef.current) return;
          paymentCompletedRef.current = true;
          try {
            razorpayInstance.close();
          } catch { /* modal may already be dismissed */ }
          try {
            await completeOrder('gateway', {
              orderId:   order.order_id,
              paymentId: info.captured_payment_id,
            });
          } catch (err) {
            console.error('Post-poll completion failed:', err);
            setPaymentError('Payment received but order finalisation failed. Contact support.');
          } finally {
            setProcessing(false);
          }
        },
      });

      razorpayInstance.open();
    } catch (error) {
      stopPolling?.();
      console.error('Checkout failed:', error);
      const { message, detail } = describePaymentError(error);
      setPaymentError(message);
      setPaymentErrorDetail(detail);
      setProcessing(false);
    }
  };

  const handleCheckout = async () => {
    if (!(await preCheck())) return;
    const usingCredits = paymentMethod === 'credits';
    if (usingCredits && !canUseCredits) {
      toast.error(
        membershipActive
          ? `Not enough credits. Add ${formatCurrency(creditShortfall)} more to use credits.`
          : 'Activate a membership to use credits.'
      );
      return;
    }

    if (usingCredits && membershipActive) {
      try {
        setProcessing(true);
        
        // Use credits via backend API
        await redeemCredits(
          cart.total,
          'order',
          `shop-order-${Date.now()}`,
          'Shop order payment'
        );
        
        // Update local membership state
        setMembership(prev => ({
          ...prev,
          balance_remaining: prev.balance_remaining - cart.total
        }));

        await completeOrder('credits');
      } catch (error) {
        const errorMessage = error.response?.data?.detail || 'Failed to use credits. Please try again.';
        toast.error(errorMessage);
        // Reload membership to get accurate balance
        const membershipData = await getMembership();
        setMembership(membershipData);
      } finally {
        setProcessing(false);
      }

      return;
    }

    await handleGatewayPayment();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-sand flex items-center justify-center pb-24">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E54562]"></div>
      </div>
    );
  }

  if (!cart) {
    return null;
  }

  return (
    <div
      className="min-h-screen bg-[#F9FAFB] text-[#2B3A67]"
      // Room for the bottom nav plus the floating checkout button above it.
      style={{ fontFamily: "'Outfit', sans-serif", paddingBottom: 'calc(var(--bottom-nav-h) + 6rem)' }}
    >
      <header className="sticky top-0 z-30 bg-[#F9FAFB]/90 backdrop-blur-md border-b border-gray-100 pt-safe">
        <div className="flex items-center gap-3 px-6 py-5">
          <button
            data-testid="back-button"
            onClick={() => navigate('/cart')}
            className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-100"
          >
            <i className="ph-bold ph-arrow-left text-xl"></i>
          </button>
          <div>
            <h1 data-testid="checkout-title" className="font-extrabold text-2xl">Checkout</h1>
            <p className="text-xs text-gray-400">One final step to confirm.</p>
          </div>
        </div>
      </header>

      <main className="px-6 pt-6 space-y-6">
        <section className="bg-white rounded-2xl p-5 shadow-md border border-gray-100" data-testid="checkout-summary">
          <h2 data-testid="checkout-summary-title" className="font-extrabold text-lg mb-4">Order Summary</h2>
          <div className="space-y-3">
            {cart.items.map((item, index) => (
              <div key={item.id} data-testid={`checkout-item-${index}`} className="flex justify-between">
                <div>
                  <p data-testid={`checkout-item-name-${index}`} className="font-bold text-sm">
                    {item.product_name}
                  </p>
                  <p data-testid={`checkout-item-details-${index}`} className="text-xs text-gray-400">
                    {item.variant_details} × {item.quantity}
                  </p>
                </div>
                <p data-testid={`checkout-item-subtotal-${index}`} className="font-bold">
                  {formatCurrency(item.subtotal)}
                </p>
              </div>
            ))}
          </div>
          <div className="border-t border-dashed border-gray-200 mt-4 pt-4 flex justify-between font-extrabold">
            <span data-testid="checkout-total-label">Total</span>
            <span data-testid="checkout-total">{formatCurrency(cart.total)}</span>
          </div>
        </section>

        <section className="bg-white rounded-2xl p-5 shadow-md border border-gray-100" data-testid="checkout-fulfilment">
          <h2 className="font-extrabold text-lg mb-4">Delivery</h2>
          <div className="grid grid-cols-2 gap-2 mb-4" role="radiogroup" aria-label="How you'll get your order">
            {[
              { id: 'delivery', label: 'Deliver to me', icon: 'ph-truck' },
              { id: 'pickup', label: 'Collect at an event', icon: 'ph-storefront' },
            ].map((o) => (
              <button key={o.id} type="button" role="radio" aria-checked={fulfilmentMethod === o.id}
                onClick={() => setFulfilmentMethod(o.id)}
                className={`rounded-xl border-2 p-3 text-sm font-bold flex items-center gap-2 ${
                  fulfilmentMethod === o.id ? 'border-[#2B3A67] bg-[#2B3A67]/5' : 'border-gray-100 text-gray-500'}`}
                data-testid={`fulfilment-${o.id}`}>
                <i className={`ph-bold ${o.icon} text-lg`} /> {o.label}
              </button>
            ))}
          </div>
          {fulfilmentMethod === 'delivery' ? (
            <AddressPicker value={addressId} onChange={setAddressId} />
          ) : (
            <div>
              <label htmlFor="pickup-note" className="text-xs font-bold text-gray-500">Which event will you collect it at? (optional)</label>
              <input id="pickup-note" value={pickupNote} onChange={(e) => setPickupNote(e.target.value)} maxLength={200}
                placeholder="e.g. Backyard Premier League final"
                className="mt-1 w-full border-2 border-gray-100 focus:border-[#2B3A67] rounded-xl px-3 py-2.5 text-sm outline-none"
                data-testid="pickup-note" />
              <p className="text-xs text-gray-400 mt-1">We'll message your mobile number when it's ready.</p>
            </div>
          )}
        </section>

        <section className="bg-white rounded-2xl p-5 shadow-md border border-gray-100" data-testid="checkout-payment">
          <h2 data-testid="checkout-payment-title" className="font-extrabold text-lg mb-4">Payment Method</h2>
          <div className="space-y-4">
            {(membershipActive || canPurchaseMembership()) && (
            <div className="bg-[#F4D341]/10 rounded-2xl p-4 border border-[#F4D341]" data-testid="checkout-credits-card">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#F4D341] rounded-full flex items-center justify-center">
                    <i className="ph-bold ph-wallet text-[#2B3A67]"></i>
                  </div>
                  <div>
                    <p data-testid="checkout-credits-label" className="font-bold">Member credits</p>
                    <p data-testid="checkout-credits-status" className="text-sm text-gray-600 mt-1">
                      {membershipActive
                        ? canUseCredits
                          ? 'You can cover this order fully with credits.'
                          : `You need ${formatCurrency(creditShortfall)} more credits.`
                        : 'No active membership linked to this account.'}
                    </p>
                  </div>
                </div>
                <span data-testid="checkout-credits-balance" className="font-bold">
                  {formatCurrency(availableCredits)}
                </span>
              </div>
              {!membershipActive && (
                <button
                  data-testid="checkout-credits-cta"
                  onClick={() => navigate('/memberships')}
                  className="mt-4 text-sm font-bold text-[#E54562]"
                >
                  View memberships
                </button>
              )}
            </div>
            )}

            <label
              data-testid="payment-method-gateway-option"
              className={`flex items-center gap-3 border-2 rounded-2xl p-4 cursor-pointer ${
                paymentMethod === 'gateway' ? 'border-[#E54562] bg-[#E54562]/10' : 'border-gray-200'
              }`}
            >
              <input
                data-testid="payment-method-gateway-radio"
                type="radio"
                name="payment-method"
                checked={paymentMethod === 'gateway'}
                onChange={() => setPaymentMethod('gateway')}
                className="h-4 w-4 accent-[#E54562]"
              />
              <div className="flex-1">
                <p data-testid="checkout-payment-label" className="font-bold">Pay via Razorpay</p>
                <p data-testid="checkout-payment-note" className="text-xs text-gray-500">
                  Secure checkout powered by Razorpay.
                </p>
              </div>
              <i className="ph-bold ph-credit-card text-gray-400"></i>
            </label>

            <label
              data-testid="payment-method-credits-option"
              className={`flex items-center gap-3 border-2 rounded-2xl p-4 cursor-pointer ${
                paymentMethod === 'credits' ? 'border-[#3AAFA9] bg-[#3AAFA9]/10' : 'border-gray-200'
              } ${!canUseCredits ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input
                data-testid="payment-method-credits-radio"
                type="radio"
                name="payment-method"
                checked={paymentMethod === 'credits'}
                onChange={() => setPaymentMethod('credits')}
                disabled={!canUseCredits}
                className="h-4 w-4 accent-[#3AAFA9]"
              />
              <div className="flex-1">
                <p className="font-bold">Pay with credits</p>
                <p className="text-xs text-gray-500">Use your balance instead of the payment gateway.</p>
              </div>
              <i className="ph-bold ph-wallet text-gray-400"></i>
            </label>

            {paymentError && (
              <div
                data-testid="checkout-payment-error"
                className="bg-red-50 border border-red-200 text-red-600 rounded-2xl p-3 text-xs font-semibold mt-3"
              >
                {paymentError}
                {paymentErrorDetail && (
                  <p className="mt-1 font-normal text-[10px] text-red-400 break-words" data-testid="checkout-payment-error-detail">
                    {paymentErrorDetail}
                  </p>
                )}
              </div>
            )}

            {user?.review_mode && (
              <ReviewModeBanner
                disabled={processing}
                amountLabel={formatCurrency(cart?.total || 0)}
                onBypass={async () => {
                  if (!(await preCheck())) return;
                  try {
                    setProcessing(true);
                    await completeOrder('free');
                  } catch (err) {
                    setPaymentError(err?.response?.data?.detail || 'Bypass failed.');
                  } finally {
                    setProcessing(false);
                  }
                }}
              />
            )}
          </div>
        </section>
      </main>

      <div
        className="fixed left-0 right-0 px-6 z-[1001]"
        style={{ bottom: 'calc(var(--bottom-nav-h) + 0.75rem)' }}
        data-testid="checkout-cta-bar"
      >
        <button
          data-testid="place-order-button"
          onClick={handleCheckout}
          disabled={processing}
          className="w-full py-4 rounded-2xl bg-[#E54562] text-white font-extrabold shadow-lg"
        >
          {processing ? 'Processing...' : 'Checkout Now'}
        </button>
      </div>
      {gate.sheet}
    </div>
  );
}